import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Building2, FileText, Calendar, Clock, CheckCircle2, AlertCircle, MapPin, ArrowRight, Loader2, ClipboardList } from 'lucide-react';
import api from '../services/api';
import { clientActionPlanService } from '../services';
import { useClientPortal } from '../contexts/ClientPortalContext';

// Import the Portfolio Dashboard for multi-site users
import ClientPortfolioDashboardPage from './ClientPortfolioDashboardPage';

/**
 * Client Portal Dashboard - Phase 2.2A/B + Multi-Site Enhancement
 * Automatically detects if user has multiple sites:
 * - Multi-site users: Shows portfolio dashboard
 * - Single-site users: Shows simplified site dashboard
 */
const ClientDashboardPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { apEnabled } = useClientPortal();
  const [portalStatus, setPortalStatus] = useState(null);
  const [accessSummary, setAccessSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isMultiSite, setIsMultiSite] = useState(false);
  const [apSummary, setApSummary] = useState(null);
  const [apLoading, setApLoading] = useState(true);
  
  // Use environment variable or strip "(Staging)" from org name
  const displayOrgName = process.env.REACT_APP_DISPLAY_NAME || 
    (user?.organisation_name?.replace(/\s*\(Staging\)\s*/i, '') || 'Your Organisation');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statusResponse, accessResponse] = await Promise.all([
          api.get('/client/portal-status'),
          api.get('/client/me/access-summary')
        ]);
        setPortalStatus(statusResponse.data);
        setAccessSummary(accessResponse.data);
        
        // Determine if user has multiple sites
        const effectiveSiteCount = accessResponse.data?.effective_site_count || 0;
        setIsMultiSite(effectiveSiteCount > 1);
      } catch (error) {
        console.error('Failed to fetch portal data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Action Plan oversight for single-site clients only — skipped when AP is disabled.
  useEffect(() => {
    if (loading || isMultiSite || !apEnabled) return;
    let cancelled = false;
    setApLoading(true);
    clientActionPlanService.analytics({})
      .then(res => { if (!cancelled) setApSummary(res.summary); })
      .catch(() => { if (!cancelled) setApSummary(null); })
      .finally(() => { if (!cancelled) setApLoading(false); });
    return () => { cancelled = true; };
  }, [loading, isMultiSite, apEnabled]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin h-8 w-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  // Multi-site users get the portfolio dashboard
  if (isMultiSite) {
    return <ClientPortfolioDashboardPage />;
  }

  // Single-site users get the simplified dashboard
  return (
    <div className="space-y-6" data-testid="client-dashboard-single-site">
      {/* Welcome Section */}
      <div className="bg-gradient-to-r from-emerald-500 to-teal-600 rounded-xl p-6 text-white">
        <h1 className="text-2xl font-bold mb-2">
          Welcome, {user?.first_name}!
        </h1>
        <p className="text-emerald-100">
          Access your audit reports and view upcoming scheduled audits for your sites.
        </p>
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600 flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              Organisation
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-lg font-semibold text-slate-900">
              {displayOrgName}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600 flex items-center gap-2">
              <MapPin className="h-4 w-4 text-emerald-500" />
              Site Access
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-lg font-semibold text-emerald-600">
              {accessSummary?.effective_site_count || 0} sites
            </p>
            <p className="text-xs text-muted-foreground">
              {accessSummary?.assigned_groups?.length || 0} groups • {accessSummary?.assigned_sites?.length || 0} direct
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600 flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-emerald-500" />
              Portal Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-lg font-semibold text-emerald-600 capitalize">
              {portalStatus?.status || 'Active'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600 flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Last Login
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-lg font-semibold text-slate-900">
              Just now
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Feature Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
        <Card className="border border-slate-200 hover:border-emerald-300 transition-colors flex flex-col">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-slate-800">
              <FileText className="h-5 w-5 text-emerald-600" />
              Reports
            </CardTitle>
            <CardDescription>
              Download published audit reports for your sites
            </CardDescription>
          </CardHeader>
          <CardContent className="mt-auto">
            <Link to="/client/reports">
              <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="go-to-reports-btn">
                Browse Reports
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
          </CardContent>
        </Card>

        <Card className="border border-slate-200 hover:border-emerald-300 transition-colors flex flex-col">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-slate-800">
              <Calendar className="h-5 w-5 text-emerald-600" />
              Upcoming Audits
            </CardTitle>
            <CardDescription>
              View scheduled audits for your sites
            </CardDescription>
          </CardHeader>
          <CardContent className="mt-auto">
            <Link to="/client/audits">
              <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="go-to-audits-btn">
                View Schedule
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
          </CardContent>
        </Card>

        {/* Action Plan oversight — only shown when AP is enabled for this customer. */}
        {apEnabled === true && (
        <Card className="border border-slate-200 hover:border-emerald-300 transition-colors flex flex-col" data-testid="single-site-ap-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-slate-800">
              <ClipboardList className="h-5 w-5 text-emerald-600" />
              Action Plan
            </CardTitle>
            <CardDescription>
              {apLoading ? 'Loading…' : apSummary
                ? `${apSummary.outstanding} outstanding${apSummary.overdue > 0 ? ` · ${apSummary.overdue} overdue` : ''}`
                : 'View corrective actions for your site'}
            </CardDescription>
          </CardHeader>
          <CardContent className="mt-auto">
            <Link to="/client/action-plans">
              <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="go-to-action-plans-btn">
                View Action Plan
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
          </CardContent>
        </Card>
        )}
      </div>

      {/* Info Box */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <div className="bg-blue-100 rounded-full p-2">
              <AlertCircle className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-medium text-blue-900 mb-1">
                Access Information
              </h3>
              <p className="text-sm text-blue-700">
                Welcome to AbbeyZone. Access your audit reports, performance insights, 
                and scheduled audits. For any questions about your audits, 
                please contact your account manager.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ClientDashboardPage;
